import { defineStore } from "pinia";
import { computed, ref } from "vue";

import { api, ApiError } from "@/api/client";
import type { AgentView, ControlSnapshot, RemoteSession } from "@/api/types";

// State machine for the live machines list. Initial seed comes from
// /api/agents + /api/sessions; subsequent updates arrive as full
// `control_snapshot` envelopes on `/ws/control` (§10.7).
type StreamState =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "ready"; lastEvent: string; serverTime: string }
  | { kind: "error"; message: string };

// AgentView now carries os/arch/version/capabilities, so the snapshot agents
// and the REST agents share one type.
type AgentSummary = AgentView;

export const useMachinesStore = defineStore("machines", () => {
  const agents = ref<AgentSummary[]>([]);
  const sessions = ref<RemoteSession[]>([]);
  const state = ref<StreamState>({ kind: "idle" });

  let socket: WebSocket | null = null;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let reconnectDelay = 1000;
  let stopped = false;

  function applySnapshot(snapshot: ControlSnapshot) {
    agents.value = snapshot.agents.map((a) => ({ ...a }));
    sessions.value = [...snapshot.remote_sessions];
    state.value = {
      kind: "ready",
      lastEvent: snapshot.event,
      serverTime: snapshot.server_time,
    };
    reconnectDelay = 1000;
  }

  async function initialFetch() {
    state.value = { kind: "loading" };
    try {
      const [agentsResp, sessionsResp] = await Promise.all([
        api.agents.list(),
        api.sessions.listAll(),
      ]);
      agents.value = agentsResp.agents.map((a) => ({ ...a }));
      sessions.value = sessionsResp.sessions;
      state.value = {
        kind: "ready",
        lastEvent: "rest_seed",
        serverTime: new Date().toISOString(),
      };
    } catch (err) {
      state.value = { kind: "error", message: describeError(err) };
    }
  }

  function connectStream() {
    if (socket || stopped) return;
    const proto = window.location.protocol === "https:" ? "wss" : "ws";
    const url = `${proto}://${window.location.host}/ws/control`;
    const ws = new WebSocket(url);
    socket = ws;
    ws.onmessage = (ev) => {
      try {
        const env = JSON.parse(typeof ev.data === "string" ? ev.data : "");
        if (env && env.type === "control_snapshot" && env.payload) {
          applySnapshot(env.payload as ControlSnapshot);
        }
      } catch (e) {
        console.warn("control_snapshot parse failed", e);
      }
    };
    ws.onclose = () => {
      socket = null;
      if (!stopped) scheduleReconnect();
    };
    ws.onerror = () => {
      // onclose follows; do nothing here to avoid double-scheduling.
    };
  }

  function scheduleReconnect() {
    if (reconnectTimer) return;
    const delay = Math.min(reconnectDelay, 30_000);
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null;
      reconnectDelay = Math.min(reconnectDelay * 2, 30_000);
      connectStream();
    }, delay);
  }

  async function start() {
    stopped = false;
    await initialFetch();
    if (state.value.kind === "ready") {
      connectStream();
    }
  }

  function stop() {
    stopped = true;
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
    if (socket) {
      socket.close();
      socket = null;
    }
  }

  const onlineAgents = computed(() =>
    agents.value.filter((a) => a.presence === "online" && a.status === "enabled"),
  );

  const sessionsForAgent = (agentId: string) =>
    sessions.value.filter((s) => s.agent_id === agentId && s.status !== "stopped");

  function describeError(err: unknown): string {
    if (err instanceof ApiError) return `${err.code} (HTTP ${err.status})`;
    if (err instanceof Error) return err.message;
    return "unknown error";
  }

  return {
    agents,
    sessions,
    state,
    onlineAgents,
    sessionsForAgent,
    start,
    stop,
  };
});
